<script lang="ts">
  // Publish flow, Revit-only. Pick a .rfa → the plugin extracts its geometry, parameters, category,
  // units, saved-in year and a language guess → the author reviews a live 3D preview and edits the
  // metadata → publish. The heavy .rfa goes straight from the author's machine to storage; only the
  // preview and metadata pass through the API.

  import { transport } from "@/transport";
  import { auth } from "@/stores/auth.svelte";
  import {
    categoryLabel,
    languageLabel,
    KNOWN_CATEGORIES,
    AUTHORING_LANGUAGES,
    LOD_LEVELS,
  } from "./categories";
  import FamilyViewer3D from "./FamilyViewer3D.svelte";
  import OmniClassPicker from "./OmniClassPicker.svelte";
  import type {
    FamilyPackage,
    FamilyTree,
    PublishRequest,
    PublishResult,
  } from "./types";
  import { PICKER_TIMEOUT_MS } from "@/transport/types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";
  import {
    AppButton,
    AppDialog,
    AppInput,
    AppSelect,
    AppTextarea,
  } from "@/components/ui";

  let {
    onclose,
    onpublished,
  }: { onclose?: () => void; onpublished?: (slug: string) => void } = $props();

  type Step = "choose" | "extracting" | "form" | "uploading" | "done";
  let step = $state<Step>("choose");
  let error = $state("");

  let pkg = $state<FamilyPackage | null>(null);
  let result = $state<PublishResult | null>(null);

  // Editable form fields, seeded from the extracted package.
  let form = $state({
    name: "",
    category: "",
    description: "",
    tags: "",
    subcategory: "",
    omniClass: "",
    // Kept beside the code rather than derived from it: only the picker (and the family itself) can
    // say what a code is called, and a hand-typed code has no title at all.
    omniClassTitle: null as string | null,
    language: "" as string,
    visibility: "public" as "public" | "org",
    // A string, like every other AppSelect-bound field here (AppSelect's value prop is string) —
    // converted to a number only when building the publish request. Empty, not pre-selected to
    // LOD_LEVELS[0]: publishing requires an explicit choice, not a default nobody consciously made.
    lod: "" as string,
  });

  // Subcategories already in use, offered as suggestions. Free text is still allowed — this is the
  // one level of the tree the author names — but picking an existing one is how a library avoids
  // growing "Swings", "swings" and "Swing " as three sibling folders.
  let knownSubcategories = $state<string[]>([]);

  async function loadSubcategories(category: string) {
    if (!category) {
      knownSubcategories = [];
      return;
    }
    try {
      const tree = await transport.invoke<FamilyTree>("BrowseFamilyTree", {
        category,
      });
      knownSubcategories =
        tree.level === "subcategory" ? tree.nodes.map((n) => n.key) : [];
    } catch {
      // Suggestions are a nicety; typing still works without them.
      knownSubcategories = [];
    }
  }

  $effect(() => {
    void loadSubcategories(form.category);
  });

  // Where the family will live. "Organization" is offered only to a member of one — publishing to an
  // organization you are not in is refused by the server, so showing the option would be an invitation
  // to an error message.
  //
  // TEMPORARY: "private" is gone. Uploading a family only its author can see gives nothing back that
  // the plugin cannot already do by pointing at a folder, so publishing means either your
  // organisation or the marketplace. The server refuses private outright — see PublishService — and
  // this list only spares the user finding that out after filling in the form.
  const visibilityOptions = $derived.by(() => {
    const options: { value: "public" | "org"; label: string; hint: string }[] =
      [
        {
          value: "public",
          label: t("publish.visibilityPublic"),
          hint: t("publish.visibilityPublicHint"),
        },
      ];
    if (auth.state.organizationId) {
      options.push({
        value: "org",
        label: auth.state.organizationName || t("publish.visibilityOrg"),
        hint: t("publish.visibilityOrgHint"),
      });
    }
    return options;
  });

  // The category list always includes whatever was detected, even if it is not in the curated set.
  const categoryOptions = $derived.by(() => {
    const set = new Set<string>(KNOWN_CATEGORIES);
    if (pkg?.revitCategory) set.add(pkg.revitCategory);
    return [...set];
  });

  async function chooseFile() {
    error = "";
    let paths: string[] = [];
    try {
      const res = await transport.invoke<{ paths: string[] }>(
        "PickFiles",
        { multiple: false },
        { timeoutMs: PICKER_TIMEOUT_MS },
      );
      paths = res.paths ?? [];
    } catch (e) {
      error = messageOf(e);
      return;
    }
    if (!paths.length) return; // cancelled

    step = "extracting";
    try {
      const extracted = await transport.invoke<FamilyPackage>(
        "ExtractFamilyPackage",
        {
          localPath: paths[0],
        },
      );
      if (!extracted.ok) {
        error = extracted.error ?? t("publish.extractFailed");
        step = "choose";
        return;
      }
      pkg = extracted;
      form = {
        name: extracted.suggestedName,
        category: extracted.revitCategory || KNOWN_CATEGORIES[0] || "",
        description: "",
        tags: "",
        subcategory: "",
        // Read from the family. Editable, because plenty of families carry no classification and the
        // author may know it — but pre-filled, because retyping what Revit already knows is busywork.
        omniClass: extracted.omniClassCode ?? "",
        omniClassTitle: extracted.omniClassTitle ?? null,
        language: extracted.suggestedLanguage ?? "",
        // Public by default even for a member of an organization: publishing is a deliberate act, and
        // defaulting to the narrower shelf would quietly bury families their author meant to share.
        visibility: "public",
        lod: "",
      };
      step = "form";
    } catch (e) {
      error = messageOf(e);
      step = "choose";
    }
  }

  async function publish() {
    if (!pkg || !form.name.trim() || !form.lod) return;
    error = "";
    step = "uploading";

    const request: PublishRequest = {
      name: form.name.trim(),
      revitCategory: form.category,
      subcategory: form.subcategory.trim() || null,
      sourceFileName: pkg.sourceFileName,
      omniClassCode: form.omniClass.trim() || null,
      // The title travels with the code that owns it: the picker clears it the moment the code is
      // edited by hand, so this can never carry one code's name next to another code.
      omniClassTitle: form.omniClassTitle,
      description: form.description.trim(),
      tags: form.tags
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      authoringLanguage: form.language || null,
      unitSystem: pkg.unitSystem,
      visibility: form.visibility,
      lod: Number(form.lod),
      localPath: pkg.localPath,
      revitYear: pkg.revitYear,
      sha256: pkg.sha256,
      sizeBytes: pkg.sizeBytes,
      // The mesh and thumbnail are deliberately NOT sent back: inside Revit that would be a large
      // web→host postMessage the bridge drops, so the publish request would never arrive. The host
      // reuses them from the package it extracted (keyed by localPath). In the browser there is no
      // publish path at all, so nothing depends on shipping them here.
      mesh: null,
      thumbnailPngBase64: null,
      parameters: pkg.parameters,
      types: pkg.types,
    };

    try {
      const res = await transport.invoke<PublishResult>(
        "UploadFamily",
        request,
      );
      result = res;
      if (res.ok) {
        step = "done";
      } else {
        error = res.error ?? t("publish.failed");
        step = "form";
      }
    } catch (e) {
      error = messageOf(e);
      step = "form";
    }
  }

  function viewPublished() {
    if (result?.slug) onpublished?.(result.slug);
  }

  function messageOf(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
  }

  // Why the Publish button below is disabled, so it is not just inert — the LOD field especially is
  // easy to miss, sitting between the category and the OmniClass picker. Null while uploading: that
  // disablement is expected and self-explains via the label already switching to "Publishing…".
  const publishDisabledReason = $derived.by(() => {
    if (step === "uploading") return null;
    if (!form.name.trim()) return t("publish.nameRequired");
    if (!form.lod) return t("publish.lodRequired");
    return null;
  });

  const sizeLabel = $derived.by(() => {
    const bytes = pkg?.sizeBytes ?? 0;
    return bytes > 1_000_000
      ? `${(bytes / 1_048_576).toFixed(1)} MB`
      : `${Math.ceil(bytes / 1024)} KB`;
  });
</script>

<AppDialog
  size="sm"
  title={t("publish.title")}
  closeLabel={t("publish.close")}
  closeOnOverlay={false}
  closeOnEscape={false}
  onclose={() => onclose?.()}
>
  <!-- Step 1: choose a file -->
  {#if step === "choose"}
    <p class="text-sm text-ink-secondary">{t("publish.chooseHint")}</p>
    <AppButton variant="primary" size="lg" block onclick={chooseFile}>
      {t("publish.choose")}
    </AppButton>
  {:else if step === "extracting"}
    <!-- Step 2: extracting -->
    <p class="text-sm text-ink-muted py-8 text-center">
      {t("publish.extracting")}
    </p>
  {:else if (step === "form" || step === "uploading") && pkg}
    <!-- Step 3: review + edit -->
    <div
      class="aspect-video bg-panel rounded-surface overflow-hidden flex items-center justify-center"
    >
      {#if pkg.mesh?.available}
        <FamilyViewer3D mesh={pkg.mesh} class="w-full h-full" />
      {:else}
        <span class="text-ink-muted text-xs px-4 text-center"
          >{t("publish.noGeometry")}</span
        >
      {/if}
    </div>

    <div class="flex flex-col gap-3">
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.name")}</span
        >
        <AppInput bind:value={form.name} type="text" />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.category")}</span
        >
        <AppSelect bind:value={form.category}>
          {#each categoryOptions as c (c)}
            <option value={c}>
              {categoryLabel(c, locale.value as SupportedLocale)}
            </option>
          {/each}
        </AppSelect>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.lod")}</span
        >
        <AppSelect bind:value={form.lod}>
          <option value="" disabled>—</option>
          {#each LOD_LEVELS as l (l)}
            <option value={String(l)}>LOD {l}</option>
          {/each}
        </AppSelect>
        <span class="text-xs text-ink-muted">{t("publish.lodHint")}</span>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.language")}</span
        >
        <AppSelect bind:value={form.language}>
          <option value="">—</option>
          {#each AUTHORING_LANGUAGES as l (l)}
            <option value={l}>{languageLabel(l)}</option>
          {/each}
        </AppSelect>
      </label>

      <!-- OmniClass, read from the family rather than asked for, and pickable when it is not
           there. Shown even when empty so the author can see it is empty — a classification
           nobody notices missing never gets added. -->
      <OmniClassPicker
        bind:code={form.omniClass}
        bind:title={form.omniClassTitle}
        category={form.category}
      />

      <!-- The one level of the tree the author names. Suggestions come from what is already
           filed under this category, so an existing folder is reused rather than re-spelled. -->
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.subcategory")}</span
        >
        <AppInput
          bind:value={form.subcategory}
          type="text"
          list="apibim-subcategories"
          placeholder={t("publish.subcategoryHint")}
        />
        <datalist id="apibim-subcategories">
          {#each knownSubcategories as sc (sc)}
            <option value={sc}></option>
          {/each}
        </datalist>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.description")}</span
        >
        <AppTextarea bind:value={form.description} rows={2} />
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-ink-muted"
          >{t("publish.tags")}</span
        >
        <AppInput
          bind:value={form.tags}
          type="text"
          placeholder={t("publish.tagsHint")}
        />
      </label>

      <!-- Who will see it. Radio cards rather than a select: this is the one field on the form
           whose wrong answer is not a typo but a disclosure, so each option states what it
           means instead of making the author infer it from one word. -->
      <fieldset class="flex flex-col gap-1">
        <legend class="text-xs font-medium text-ink-muted mb-1"
          >{t("publish.visibility")}</legend
        >
        {#each visibilityOptions as o (o.value)}
          <label
            class="flex items-start gap-2 rounded-control border px-3 py-2 cursor-pointer transition-colors {form.visibility ===
            o.value
              ? 'border-accent bg-panel'
              : 'border-line hover:border-accent'}"
          >
            <input
              bind:group={form.visibility}
              type="radio"
              value={o.value}
              class="mt-0.5 accent-[var(--brand-accent)]"
            />
            <span class="flex flex-col min-w-0">
              <span class="text-sm truncate">{o.label}</span>
              <span class="text-xs text-ink-muted">{o.hint}</span>
            </span>
          </label>
        {/each}
      </fieldset>
    </div>

    <!-- Facts read from the file — not editable. -->
    <dl
      class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs text-ink-muted"
    >
      <dt>{t("detail.revitYears")}</dt>
      <dd class="tabular-nums text-ink">{pkg.revitYear}</dd>
      <dt>{t("detail.units")}</dt>
      <dd class="text-ink">{pkg.unitSystem ?? "—"}</dd>
      <dt>{t("detail.parameters")}</dt>
      <dd class="text-ink">{pkg.parameters.length}</dd>
      <dt>{t("publish.size")}</dt>
      <dd class="text-ink">{sizeLabel}</dd>
    </dl>

    {#if error}
      <p class="text-xs text-error text-center">{error}</p>
    {/if}

    <!-- display: contents so the wrapper does not affect layout — a disabled native button often
         does not fire hover/title at all in Chromium, but the wrapping element still does. -->
    <span class="contents" title={publishDisabledReason ?? undefined}>
      <AppButton
        variant="primary"
        size="lg"
        block
        disabled={step === "uploading" || !form.name.trim() || !form.lod}
        onclick={publish}
      >
        {step === "uploading" ? t("publish.publishing") : t("publish.publish")}
      </AppButton>
    </span>
  {:else if step === "done" && result}
    <!-- Step 4: done -->
    <div class="py-4 text-center flex flex-col gap-2">
      <p class="text-sm font-medium">
        {result.status === "pending" ? t("publish.pending") : t("publish.done")}
      </p>
      <div class="flex gap-2 justify-center mt-2">
        {#if result.status === "approved"}
          <AppButton variant="primary" onclick={viewPublished}>
            {t("publish.view")}
          </AppButton>
        {/if}
        <AppButton variant="secondary" onclick={() => onclose?.()}>
          {t("publish.close")}
        </AppButton>
      </div>
    </div>
  {/if}

  {#if error && step === "choose"}
    <p class="text-xs text-error text-center">{error}</p>
  {/if}
</AppDialog>
